/**
 * Sections du PDF rapport , Ultra Mesure
 *
 * Chaque fonction retourne un Content[] pdfmake représentant une section.
 * L'assemblage final se fait dans pdf-generator.ts.
 */

import type { Content, ContentTable, TableCell } from "pdfmake/interfaces";
import type { OfflineRapport, OfflineSite, OfflineMesure, OfflinePin, OfflinePhoto } from "@/core/offline/db";
import type { PinExportData, ZoneExportData } from "@/modules/corrosion/export/data-fetcher";
import { formatReseau } from "@/modules/corrosion/types";
import {
  PDF_COLORS,
  PDF_FONT_SIZES,
  CORROSION_COLORS,
  CORROSION_BG_COLORS,
  CORROSION_LEGEND_FULL,
} from "./pdf-styles";
import { PIN_PLAN_COLORS, NON_MESURE_PLAN_HEX } from "@/modules/corrosion/palette";
import { PLAN_PDF_FIT_PT } from "@/modules/corrosion/export/plan-layout";
import { tiptapToPdfmake } from "./tiptap-to-pdfmake";

// ─── Types ───────────────────────────────────────────────────

/** Info photo pour le PDF : dataUrl + orientation détectée */
export type PhotoRenderInfo = {
  dataUrl: string;
  /** true si la photo est portrait (hauteur > largeur) , déclenche un layout compact */
  isPortrait: boolean;
};

// ─── Helpers ─────────────────────────────────────────────────

/** Formate un nombre avec 2 décimales (ex: 2.90) */
function fmt(val: number | null | undefined): string {
  if (val === null || val === undefined) return "\u2014";
  // 2 decimales max, sans zero inutile. Separateur decimal : point (defaut JS).
  // Exemples : 2.65 -> "2.65", 2.50 -> "2.5", 2.00 -> "2".
  return parseFloat(val.toFixed(2)).toString();
}

/** Formate un pourcentage avec 1 décimale */
function fmtPct(val: number | null | undefined): string {
  if (val === null || val === undefined) return "\u2014";
  // 1 decimale max, zero inutile supprime. Separateur decimal : point.
  // Exemples : 4.0 -> "4%", 4.5 -> "4.5%", 12.7 -> "12.7%".
  return `${parseFloat(val.toFixed(1)).toString()}%`;
}

/** Formatte la date du jour en français */
function formatDateFr(date?: Date): string {
  const d = date ?? new Date();
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Couleur hex pour un code couleur corrosion */
function corrosionColor(code: string | null | undefined): string {
  return CORROSION_COLORS[code ?? ""] ?? PDF_COLORS.text;
}

/** Couleur de fond pour un code couleur corrosion */
function corrosionBg(code: string | null | undefined): string {
  return CORROSION_BG_COLORS[code ?? ""] ?? PDF_COLORS.white;
}

// ─── Page de couverture ──────────────────────────────────────

export function buildCoverPage(
  rapport: OfflineRapport,
  site: OfflineSite,
  logoDataUrl?: string
): Content[] {
  const content: Content[] = [];

  // Pied de page de la couverture (place EN TETE du content pour eviter qu il
  // n atterrisse sur page 2 si le contenu naturel deborde). Position absolue
  // sur la page courante (page 1), le contenu naturel coule normalement
  // par-dessus, sans interferer car il finit largement avant.
  // y=720pt : le stack fait ~60pt (encart + 3 lignes adresse), donc finit
  // vers y=780, ce qui laisse 12pt de marge avant le bord de page (842pt).
  // Plus haut casse l equilibre visuel ; plus bas et le stack deborde sur
  // page 2 (constate avec y=755).
  content.push({
    absolutePosition: { x: 40, y: 720 },
    width: 515,
    stack: [
      {
        table: {
          widths: ["*"],
          body: [[
            {
              text: "Ce document est à l’attention exclusive de son destinataire. Toute utilisation, divulgation ou reproduction de son contenu est interdite.",
              color: PDF_COLORS.blue,
              bold: true,
              alignment: "center",
              fontSize: PDF_FONT_SIZES.small,
              margin: [4, 4, 4, 4] as [number, number, number, number],
            },
          ]],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => PDF_COLORS.blue,
          vLineColor: () => PDF_COLORS.blue,
        },
        margin: [0, 0, 0, 10] as [number, number, number, number],
      },
      {
        text: "5 Allée du Paradis - 91430 IGNY  -  Tél : 06 80 30 56 97",
        alignment: "center",
        bold: true,
        fontSize: PDF_FONT_SIZES.small,
        margin: [0, 0, 0, 2] as [number, number, number, number],
      },
      {
        text: "S.A.R.L. au capital de 2 000 € - 514 915 875 R.C.S EVRY \u2013 Code APE : 7120B",
        alignment: "center",
        fontSize: PDF_FONT_SIZES.small,
        margin: [0, 0, 0, 2] as [number, number, number, number],
      },
      {
        text: "e-Mail : ultramesure@gmail.com  -  Site Web : www.ultramesure.fr",
        alignment: "center",
        fontSize: PDF_FONT_SIZES.small,
      },
    ],
  } as Content);

  // Logo centré en haut
  if (logoDataUrl) {
    content.push({
      image: logoDataUrl,
      width: 120,
      alignment: "center",
      margin: [0, 30, 0, 48] as [number, number, number, number],
    });
  } else {
    content.push({
      text: "ULTRAMESURE",
      fontSize: 24,
      bold: true,
      color: PDF_COLORS.blue,
      alignment: "center",
      margin: [0, 30, 0, 48] as [number, number, number, number],
    });
  }

  // Titre principal (éditable par rapport, avec fallback sur le texte par défaut).
  // Le texte est rendu en un seul bloc : pdfmake gère le retour à la ligne auto,
  // Renaud peut aussi forcer des sauts de ligne en tapant Entrée dans le textarea.
  const DEFAULT_TITRE =
    "Rapport de mesures d\u2019\u00e9paisseurs par ultrasons, Analyses d\u2019eau et examens m\u00e9tallographiques";
  const titreCouverture = (rapport.titreCouverture && rapport.titreCouverture.trim())
    || DEFAULT_TITRE;
  content.push({
    text: titreCouverture,
    fontSize: PDF_FONT_SIZES.coverTitle,
    bold: true,
    color: PDF_COLORS.blue,
    alignment: "center",
    margin: [0, 0, 0, 12] as [number, number, number, number],
  } as Content);

  // Sous-titre réseau (optionnel) , même bleu et même taille que le titre principal
  if (rapport.sousTitreReseau) {
    content.push({
      text: rapport.sousTitreReseau,
      fontSize: PDF_FONT_SIZES.coverTitle,
      bold: true,
      color: PDF_COLORS.blue,
      alignment: "center",
      margin: [0, 0, 0, 24] as [number, number, number, number],
    });
  } else {
    content.push({ text: " ", margin: [0, 0, 0, 16] as [number, number, number, number] } as Content);
  }

  // N° rapport
  content.push({
    text: `Rapport N\u00b0 ${rapport.numero || "\u2014"}`,
    fontSize: PDF_FONT_SIZES.heading1,
    bold: true,
    color: PDF_COLORS.blue,
    alignment: "center",
    margin: [0, 0, 0, 90] as [number, number, number, number],
  });

  // Destiné à :
  if (rapport.destinataire || rapport.destinataireAdresse) {
    content.push({
      text: "Destin\u00e9 \u00e0 :",
      fontSize: PDF_FONT_SIZES.body,
      bold: true,
      alignment: "center",
      margin: [0, 0, 0, 4] as [number, number, number, number],
    });
    if (rapport.destinataire) {
      content.push({
        text: rapport.destinataire,
        fontSize: PDF_FONT_SIZES.body,
        bold: true,
        alignment: "center",
      });
    }
    if (rapport.destinataireAdresse) {
      // L'adresse peut contenir des sauts de ligne , on les respecte
      const lines = rapport.destinataireAdresse.split(/\r?\n/);
      for (const line of lines) {
        content.push({
          text: line,
          fontSize: PDF_FONT_SIZES.body,
          alignment: "center",
        });
      }
    }
    content.push({ text: " ", margin: [0, 0, 0, 32] as [number, number, number, number] } as Content);
  }

  // Pour le site :
  content.push({
    text: "Pour le site :",
    fontSize: PDF_FONT_SIZES.body,
    bold: true,
    alignment: "center",
    margin: [0, 0, 0, 4] as [number, number, number, number],
  });
  content.push({
    text: site.name,
    fontSize: PDF_FONT_SIZES.body,
    bold: true,
    alignment: "center",
  });
  if (rapport.batiment) {
    content.push({
      text: rapport.batiment,
      fontSize: PDF_FONT_SIZES.body,
      alignment: "center",
    });
  }
  if (site.address) {
    content.push({
      text: site.address,
      fontSize: PDF_FONT_SIZES.body,
      color: PDF_COLORS.textMuted,
      alignment: "center",
    });
  }

  // Page break
  content.push({ text: "", pageBreak: "after" });

  return content;
}

// ─── Titre de section numéroté ───────────────────────────────

export function buildSectionTitle(num: number, title: string): Content {
  return {
    table: {
      widths: ["*"],
      body: [
        [
          {
            text: `${num} \u2013 ${title.toUpperCase()}`,
            fontSize: PDF_FONT_SIZES.heading1,
            bold: true,
            color: PDF_COLORS.blue,
            margin: [8, 6, 8, 6] as [number, number, number, number],
          } as TableCell,
        ],
      ],
    },
    layout: {
      fillColor: () => PDF_COLORS.grayBg,
      hLineWidth: () => 0.5,
      vLineWidth: () => 0,
      hLineColor: () => PDF_COLORS.grayBorder,
    },
    margin: [0, 16, 0, 10] as [number, number, number, number],
  } as Content;
}

// ─── Fiche de mesure individuelle ────────────────────────────

export function buildMesureFiche(
  pinData: PinExportData,
  photoDataUrls: Map<string, PhotoRenderInfo>
): Content[] {
  const { pin, mesure, photos } = pinData;
  if (!mesure) return [];

  const color = corrosionColor(mesure.codeCouleur);
  const bgColor = corrosionBg(mesure.codeCouleur);

  const content: Content[] = [];

  const tarif = Array.isArray(mesure.tarification)
    ? mesure.tarification.join(", ")
    : mesure.tarification ?? "\u2014";

  const orientationLabel =
    mesure.orientation === "H" ? "Horizontale" :
    mesure.orientation === "V" ? "Verticale" :
    mesure.orientation ?? "\u2014";

  // ─── Header : bande colorée + n° + localisation + % ───
  content.push({
    table: {
      widths: [4, "*", "auto", "auto"],
      body: [
        [
          { text: "", fillColor: color },
          {
            text: `Mesure n\u00b0${pin.number}`,
            fontSize: 11,
            bold: true,
            margin: [8, 5, 0, 5] as [number, number, number, number],
          } as TableCell,
          {
            text: formatReseau(mesure),
            fontSize: 10,
            color: PDF_COLORS.textMuted,
            margin: [8, 6, 8, 5] as [number, number, number, number],
          } as TableCell,
          {
            text: fmtPct(mesure.pourcentageCorrosion),
            fontSize: 13,
            bold: true,
            color,
            alignment: "right",
            margin: [0, 4, 8, 5] as [number, number, number, number],
          } as TableCell,
        ],
      ],
    },
    layout: {
      fillColor: (_i: number, _node: unknown, col: number) => (col === 0 ? color : bgColor),
      hLineWidth: () => 0.5,
      vLineWidth: () => 0,
      hLineColor: () => PDF_COLORS.grayBorder,
    },
    margin: [0, 8, 0, 0] as [number, number, number, number],
  } as Content);

  // ─── Body : 3 colonnes (ou 2 sans photo) ───

  // Photos disponibles
  const availablePhotos: PhotoRenderInfo[] = [];
  for (const photo of photos) {
    const info = photoDataUrls.get(photo.id);
    if (info) availablePhotos.push(info);
  }
  const hasPhotos = availablePhotos.length > 0;
  /** true si au moins une photo de la mesure est verticale , déclenche layout compact (3 fiches/page) */
  const anyPortrait = availablePhotos.some((p) => p.isPortrait);

  // Col 1 : Identification (clé-valeur)
  const idRows: TableCell[][] = [
    [
      { text: "\u00d8 Ext.", fontSize: PDF_FONT_SIZES.small, color: PDF_COLORS.textMuted } as TableCell,
      { text: mesure.diametreExt ? `${fmt(mesure.diametreExt)} mm` : "\u2014", fontSize: PDF_FONT_SIZES.body, bold: true, alignment: "right" } as TableCell,
    ],
    [
      { text: "DN", fontSize: PDF_FONT_SIZES.small, color: PDF_COLORS.textMuted } as TableCell,
      { text: mesure.dn ? `${mesure.dn}` : "\u2014", fontSize: PDF_FONT_SIZES.body, bold: true, alignment: "right" } as TableCell,
    ],
    [
      { text: "Orientation", fontSize: PDF_FONT_SIZES.small, color: PDF_COLORS.textMuted } as TableCell,
      { text: orientationLabel, fontSize: PDF_FONT_SIZES.body, bold: true, alignment: "right" } as TableCell,
    ],
    [
      { text: "Mat\u00e9riau", fontSize: PDF_FONT_SIZES.small, color: PDF_COLORS.textMuted } as TableCell,
      { text: mesure.materiau ?? "\u2014", fontSize: PDF_FONT_SIZES.body, bold: true, alignment: "right" } as TableCell,
    ],
    [
      { text: "Tarification", fontSize: PDF_FONT_SIZES.small, color: PDF_COLORS.textMuted } as TableCell,
      { text: tarif, fontSize: PDF_FONT_SIZES.body, bold: true, alignment: "right" } as TableCell,
    ],
  ];

  const colId: Content = {
    table: {
      widths: ["auto", "*"],
      body: idRows,
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingTop: () => 2,
      paddingBottom: () => 2,
      paddingLeft: () => 0,
      paddingRight: () => 4,
    },
  };

  // Col 2 : Résultats (% corrosion gros + épaisseurs)
  const colResults: Content = {
    table: {
      widths: ["*"],
      body: [
        [
          {
            stack: [
              { text: "% CORROSION", fontSize: 7, color: PDF_COLORS.textMuted, alignment: "center" },
              { text: fmtPct(mesure.pourcentageCorrosion), fontSize: 18, bold: true, color, alignment: "center", margin: [0, 2, 0, 4] as [number, number, number, number] },
            ],
            margin: [0, 0, 0, 4] as [number, number, number, number],
          } as TableCell,
        ],
        [
          {
            table: {
              widths: ["*", "*"],
              body: [
                [
                  {
                    stack: [
                      { text: "\u00c9P. MAX (mm)", fontSize: 7, color: PDF_COLORS.textMuted, alignment: "center" },
                      { text: mesure.mesureMax ? fmt(mesure.mesureMax) : "\u2014", fontSize: 10, bold: true, alignment: "center", margin: [0, 1, 0, 0] as [number, number, number, number] },
                    ],
                    margin: [2, 2, 2, 2] as [number, number, number, number],
                  } as TableCell,
                  {
                    stack: [
                      { text: "\u00c9P. MIN (mm)", fontSize: 7, color: PDF_COLORS.textMuted, alignment: "center" },
                      { text: mesure.mesureMin ? fmt(mesure.mesureMin) : "\u2014", fontSize: 10, bold: true, alignment: "center", margin: [0, 1, 0, 0] as [number, number, number, number] },
                    ],
                    margin: [2, 2, 2, 2] as [number, number, number, number],
                  } as TableCell,
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => PDF_COLORS.grayBorder,
              vLineColor: () => PDF_COLORS.grayBorder,
            },
          } as TableCell,
        ],
        [
          {
            stack: [
              { text: "\u00c9P. ORIGINE (mm)", fontSize: 7, color: PDF_COLORS.textMuted, alignment: "center" },
              { text: mesure.epaisseurOrigine ? fmt(mesure.epaisseurOrigine) : "\u2014", fontSize: 10, bold: true, alignment: "center", margin: [0, 1, 0, 0] as [number, number, number, number] },
            ],
            margin: [2, 4, 2, 2] as [number, number, number, number],
          } as TableCell,
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
      paddingLeft: () => 4,
      paddingRight: () => 4,
      fillColor: () => "#FAFAFA",
    },
  };

  // Col 3 : Photos , meme structure de colonne dans les 2 cas, seule la
  // dimension de l image change selon l orientation :
  // - Horizontales : empilees verticalement, width 130 (largeur fixe, hauteur
  //   libre selon ratio), max 2.
  // - Verticales : empilees verticalement aussi, fit [140, 130] (hauteur fixe
  //   130, largeur libre selon ratio, plafonnee a 140 pour rester dans la
  //   colonne photos), max 2.
  const photoBlock: Content = anyPortrait
    ? {
        stack: availablePhotos.slice(0, 2).map((p, i) => ({
          image: p.dataUrl,
          fit: [140, 130] as [number, number],
          margin: [0, i > 0 ? 4 : 0, 0, 0] as [number, number, number, number],
        })),
      }
    : {
        stack: availablePhotos.slice(0, 2).map((p, i) => ({
          image: p.dataUrl,
          width: 130,
          margin: [0, i > 0 ? 4 : 0, 0, 0] as [number, number, number, number],
        })),
      };

  // Assemblage body en columns. Largeurs uniformes quelle que soit l orientation
  // des photos : ID flex, Results 145pt, Photos 140pt.
  const bodyColumns: Content[] = [colId, colResults];
  const bodyWidths = hasPhotos ? ["*", 145, 140] : ["*", 170];

  if (hasPhotos) {
    bodyColumns.push({
      ...(photoBlock as object),
      margin: [4, 0, 0, 0] as [number, number, number, number],
    } as Content);
  }

  content.push({
    columns: bodyColumns.map((col, i) => ({
      ...col as object,
      width: bodyWidths[i],
    })),
    margin: [4, 4, 4, 4] as [number, number, number, number],
  } as Content);

  // ─── Footer : notes ───
  if (mesure.notes) {
    content.push({
      table: {
        widths: ["*"],
        body: [[
          {
            text: [
              { text: "Notes : ", bold: true, fontSize: PDF_FONT_SIZES.small, color: PDF_COLORS.textMuted },
              { text: mesure.notes, fontSize: PDF_FONT_SIZES.small, color: PDF_COLORS.textMuted, italics: true },
            ],
            margin: [4, 3, 4, 3] as [number, number, number, number],
          } as TableCell,
        ]],
      },
      layout: {
        hLineWidth: (i: number) => (i === 0 ? 0.5 : 0),
        vLineWidth: () => 0,
        hLineColor: () => "#F0F0F0",
      },
      margin: [0, 0, 0, 0] as [number, number, number, number],
    } as Content);
  }

  // Wrapper unbreakable : empêche pdfmake de couper la fiche entre le titre et les données
  return [{
    unbreakable: true,
    stack: content,
  } as Content];
}

// ─── Titre de zone (sous-section dans Mesures) ───────────────

export function buildZoneTitle(zoneName: string): Content {
  return {
    text: zoneName,
    fontSize: PDF_FONT_SIZES.heading2,
    bold: true,
    color: PDF_COLORS.blue,
    margin: [0, 14, 0, 6] as [number, number, number, number],
    decoration: "underline" as const,
  };
}

// ─── Plan annoté (adapté pour tenir sur la page avec le titre de zone) ───

export function buildPlanImage(planDataUrl: string): Content {
  // Le plan doit tenir sous le titre de section + titre de zone sur la même page,
  // ET au-dessus de la légende (buildPlanLegend, ~20pt). Espace dispo ≈ 660pt.
  // On utilise fit pour que pdfmake adapte automatiquement sans dépasser.
  return {
    image: planDataUrl,
    fit: PLAN_PDF_FIT_PT, // largeur utile A4 × hauteur max (légende rendue à part, en dessous)
    alignment: "center" as const,
    margin: [0, 6, 0, 0] as [number, number, number, number],
  };
}

/**
 * Légende du plan annoté, rendue à TAILLE FIXE (indépendante du format du plan).
 * Avant, la légende était cuite dans le canvas Konva (plan-export.ts) et mise à
 * l'échelle avec le plan : sur un plan PORTRAIT (étroit), elle rétrécissait et
 * les libellés se chevauchaient. Ici c'est un élément pdfmake autonome, donc
 * identique sur chaque page de plan quel que soit le format importé.
 * Couleurs dérivées de la source unique palette.ts (PIN_PLAN_COLORS).
 */
const PLAN_LEGEND_ITEMS: Array<{ color: string; label: string }> = [
  { color: PIN_PLAN_COLORS.green, label: "Bon état (< 20%)" },
  { color: PIN_PLAN_COLORS.orange, label: "Surveillance (20-29%)" },
  { color: PIN_PLAN_COLORS.red, label: "Alerte (30-39%)" },
  { color: PIN_PLAN_COLORS.dark, label: "Critique (≥ 40%)" },
  { color: NON_MESURE_PLAN_HEX, label: "Non mesuré" },
];

export function buildPlanLegend(): Content {
  const SQUARE = 7;
  // `as Content` : @types/pdfmake n'accepte pas `width` sur une colonne canvas/text
  // (limitation des types, cf. buildPhotoAnalysisSection qui caste pareil).
  const cols: Content[] = [];
  PLAN_LEGEND_ITEMS.forEach((item, i) => {
    const last = i === PLAN_LEGEND_ITEMS.length - 1;
    cols.push({
      canvas: [{ type: "rect", x: 0, y: 1.5, w: SQUARE, h: SQUARE, r: 1, color: item.color }],
      width: SQUARE,
    } as Content);
    cols.push({
      text: item.label,
      width: "auto",
      fontSize: PDF_FONT_SIZES.small,
      color: PDF_COLORS.text,
      margin: [3, 0, last ? 0 : 14, 0] as [number, number, number, number],
    } as Content);
  });
  return {
    // Spacers "*" de part et d'autre = légende centrée sous le plan.
    columns: [{ text: "", width: "*" } as Content, ...cols, { text: "", width: "*" } as Content],
    columnGap: 0,
    margin: [0, 10, 0, 0] as [number, number, number, number],
  };
}

// ─── Section Photo + Analyse ────────────────────────────────

export function buildPhotoAnalysisSection(
  title: string,
  photoDataUrl: string | undefined,
  tiptapContent: unknown,
  sectionNum: number
): Content[] {
  const content: Content[] = [];

  content.push(buildSectionTitle(sectionNum, title));

  const textContent = tiptapToPdfmake(tiptapContent);
  const textStack: Content = textContent.length > 0
    ? { stack: textContent }
    : { text: "(Aucune analyse)", fontSize: PDF_FONT_SIZES.body, color: PDF_COLORS.textLight, italics: true };

  if (photoDataUrl) {
    // Layout 2 colonnes : photo à gauche, texte à droite
    content.push({
      columns: [
        {
          image: photoDataUrl,
          width: 200,
        },
        {
          ...textStack as object,
          width: "*",
        },
      ],
      columnGap: 16,
      margin: [0, 4, 0, 8] as [number, number, number, number],
    } as Content);
  } else {
    // Pas de photo : texte seul
    content.push(textStack);
  }

  return content;
}

// ─── Tableau de synthèse ─────────────────────────────────────

/**
 * Options pour le tableau de synthèse.
 * `previous` (optionnel) : si présent, ajoute 2 colonnes "% N-X" et "Évolution"
 *  pour comparer chaque mesure à celle de la campagne antérieure.
 */
export interface SynthesisTableOptions {
  previous?: {
    previousMesureByPinId: Map<string, OfflineMesure>;
    previousLabel: string; // ex: "01/2025" ou "Nov. 2025"
  };
  // Date de prise de mesure (date de la campagne exportée). Affichée dans
  // "Mesures effectuées le …". À défaut, on retombe sur la date du jour.
  mesureDate?: Date;
}

export function buildSynthesisTable(
  pinsByZone: Array<{ zoneName: string; pins: PinExportData[] }>,
  site: OfflineSite,
  options: SynthesisTableOptions = {}
): Content[] {
  const previous = options.previous;
  const hasPrev = !!previous && previous.previousMesureByPinId.size > 0;
  const result: Content[] = [];

  // ─── Titre global ───
  result.push({
    text: "TABLEAU DE SYNTHESE DE MESURES D'\u00c9PAISSEURS PAR ULTRASONS",
    fontSize: 10,
    bold: true,
    alignment: "center",
    margin: [0, 0, 0, 4] as [number, number, number, number],
  });
  // Sous-titre : adresse + date
  const subtitleParts: string[] = [];
  if (site.name) subtitleParts.push(site.name);
  if (site.address) subtitleParts.push(site.address);
  if (subtitleParts.length > 0) {
    result.push({
      text: subtitleParts.join(" - "),
      fontSize: PDF_FONT_SIZES.body,
      alignment: "center",
      margin: [0, 0, 0, 2] as [number, number, number, number],
    });
  }
  result.push({
    text: `Mesures effectu\u00e9es le ${formatDateFr(options.mesureDate)}`,
    fontSize: PDF_FONT_SIZES.small,
    color: PDF_COLORS.textMuted,
    alignment: "center",
    margin: [0, 0, 0, 12] as [number, number, number, number],
  });

  // ─── Un tableau par zone ───
  // Si une campagne de reference existe (N-X), on ajoute 2 colonnes :
  //   * "% N-X (label)" : pourcentage de corrosion de la campagne precedente
  //   * "Evolution"     : delta en points de pourcentage, code couleur :
  //                        vert  : delta < -2 (amelioration, rare)
  //                        rouge : delta > +2 (degradation)
  //                        gris  : -2 <= delta <= +2 (stable)
  //                        "\u2014" : pas de mesure de reference pour ce pin
  const headerRow = (): TableCell[] => {
    const cells: TableCell[] = [
      { text: "US N\u00b0", bold: true, fontSize: PDF_FONT_SIZES.small, alignment: "center" },
      { text: "Pos.", bold: true, fontSize: PDF_FONT_SIZES.small, alignment: "center" },
      { text: "R\u00e9seau", bold: true, fontSize: PDF_FONT_SIZES.small },
      { text: "Diam\u00e8tre DN", bold: true, fontSize: PDF_FONT_SIZES.small, alignment: "center" },
      { text: "\u00c9p. origine\nen mm", bold: true, fontSize: PDF_FONT_SIZES.small, alignment: "center" },
      { text: "\u00c9paisseur\nmaxi en mm", bold: true, fontSize: PDF_FONT_SIZES.small, alignment: "center" },
      { text: "\u00c9paisseur Mini\nen mm", bold: true, fontSize: PDF_FONT_SIZES.small, alignment: "center" },
      { text: "% corrosion", bold: true, fontSize: PDF_FONT_SIZES.small, alignment: "center" },
    ];
    if (hasPrev && previous) {
      cells.push(
        { text: `% ${previous.previousLabel}`, bold: true, fontSize: PDF_FONT_SIZES.small, alignment: "center" },
        { text: "\u00c9volution", bold: true, fontSize: PDF_FONT_SIZES.small, alignment: "center" },
      );
    }
    return cells;
  };

  // Helper : compute la cellule "Evolution" (texte + couleur) a partir des
  // deux pourcentages. Renvoie aussi le texte "% N-X" formate.
  const evolutionCells = (
    currentPct: number | null | undefined,
    previousPct: number | null | undefined,
  ): { prevText: string; evoText: string; evoColor: string } => {
    if (currentPct === null || currentPct === undefined
        || previousPct === null || previousPct === undefined) {
      return {
        prevText: previousPct == null ? "\u2014" : fmtPct(previousPct),
        evoText: "\u2014",
        evoColor: PDF_COLORS.textMuted,
      };
    }
    const delta = currentPct - previousPct;
    const sign = delta > 0 ? "+" : "";
    const evoText = `${sign}${delta.toFixed(1)} pts`;
    let evoColor: string = PDF_COLORS.textMuted;
    if (delta > 2) evoColor = CORROSION_COLORS["red"] ?? PDF_COLORS.text;
    else if (delta < -2) evoColor = CORROSION_COLORS["green"] ?? PDF_COLORS.text;
    return { prevText: fmtPct(previousPct), evoText, evoColor };
  };

  for (const zoneGroup of pinsByZone) {
    // Titre de zone
    result.push({
      text: zoneGroup.zoneName,
      fontSize: PDF_FONT_SIZES.heading2,
      bold: true,
      color: PDF_COLORS.blue,
      margin: [0, 10, 0, 4] as [number, number, number, number],
    });

    const body: TableCell[][] = [headerRow()];

    for (const { pin, mesure } of zoneGroup.pins) {
      if (!mesure) continue;

      const bg = corrosionBg(mesure.codeCouleur);
      const pctColor = corrosionColor(mesure.codeCouleur);

      const row: TableCell[] = [
        { text: `${pin.number}`, fontSize: PDF_FONT_SIZES.small, alignment: "center", fillColor: bg },
        { text: mesure.orientation ?? "\u2014", fontSize: PDF_FONT_SIZES.small, alignment: "center", fillColor: bg },
        { text: formatReseau(mesure) || "\u2014", fontSize: PDF_FONT_SIZES.small, fillColor: bg },
        { text: mesure.dn ? `${mesure.dn}` : "\u2014", fontSize: PDF_FONT_SIZES.small, alignment: "center", fillColor: bg },
        { text: fmt(mesure.epaisseurOrigine), fontSize: PDF_FONT_SIZES.small, alignment: "center", fillColor: bg },
        { text: fmt(mesure.mesureMax), fontSize: PDF_FONT_SIZES.small, alignment: "center", fillColor: bg },
        { text: fmt(mesure.mesureMin), fontSize: PDF_FONT_SIZES.small, alignment: "center", fillColor: bg },
        { text: fmtPct(mesure.pourcentageCorrosion), fontSize: PDF_FONT_SIZES.small, alignment: "center", bold: true, color: pctColor, fillColor: bg },
      ];

      if (hasPrev && previous) {
        const prevMesure = previous.previousMesureByPinId.get(pin.id);
        const { prevText, evoText, evoColor } = evolutionCells(
          mesure.pourcentageCorrosion,
          prevMesure?.pourcentageCorrosion ?? null,
        );
        row.push(
          { text: prevText, fontSize: PDF_FONT_SIZES.small, alignment: "center", fillColor: bg },
          { text: evoText, fontSize: PDF_FONT_SIZES.small, alignment: "center", bold: true, color: evoColor, fillColor: bg },
        );
      }

      body.push(row);
    }

    // Largeurs colonnes : sans N-X = 8 colonnes ; avec N-X = 10 colonnes.
    // On retrecit les colonnes Ep. origine / max / min et la colonne flexible
    // (Reseau) pour faire de la place a "% N-X" et "Evolution".
    const widths = hasPrev
      ? [25, 22, "*", 38, 40, 42, 42, 40, 42, 48]
      : [28, 25, "*", 45, 45, 48, 48, 45];

    result.push({
      table: {
        headerRows: 1,
        widths,
        body,
      },
      layout: {
        fillColor: (rowIndex: number) => (rowIndex === 0 ? PDF_COLORS.grayBg : null),
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => PDF_COLORS.grayBorder,
        vLineColor: () => PDF_COLORS.grayBorder,
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      },
      margin: [0, 0, 0, 6] as [number, number, number, number],
    } as Content);
  }
  // Légende en fin de tableau
  result.push(buildColorLegend());

  return result;
}

// ─── Légende des couleurs ────────────────────────────────────

export function buildColorLegend(): Content {
  // Légende complète Renaud (sous le tableau de synthèse) : liste verticale avec
  // pastille de couleur + texte explicatif complet par niveau de corrosion.
  const items: Content[] = CORROSION_LEGEND_FULL.map(({ code, text }) => ({
    columns: [
      {
        canvas: [
          {
            type: "rect" as const,
            x: 0,
            y: 2,
            w: 10,
            h: 10,
            r: 2,
            color: CORROSION_COLORS[code],
          },
        ],
        width: 16,
      },
      {
        text,
        fontSize: PDF_FONT_SIZES.small,
        color: PDF_COLORS.text,
      },
    ],
    columnGap: 4,
    margin: [0, 0, 0, 4] as [number, number, number, number],
  }));

  return {
    stack: [
      {
        text: "Code couleur employ\u00e9 pour interpr\u00e9tations des r\u00e9sultats :",
        fontSize: PDF_FONT_SIZES.small,
        bold: true,
        color: PDF_COLORS.text,
        margin: [0, 6, 0, 4] as [number, number, number, number],
      },
      ...items,
    ],
    margin: [0, 6, 0, 10] as [number, number, number, number],
  };
}

// ─── Bloc signature ──────────────────────────────────────────

export function buildSignatureBlock(
  rapport: OfflineRapport,
  signatureDataUrl?: string
): Content[] {
  const out: Content[] = [];

  // Bloc signataire fer à gauche + tampon centré dans la largeur de la page
  // (3 lignes d'espace au-dessus = ~45pt avec body 10pt × lineHeight 1.2)
  out.push({
    columns: [
      {
        width: 203,
        margin: [0, 16, 0, 0] as [number, number, number, number],
        stack: [
          {
            text: rapport.signataireNom ?? "",
            fontSize: PDF_FONT_SIZES.body,
            bold: true,
          },
          {
            text: rapport.signataireFonction ?? "",
            fontSize: PDF_FONT_SIZES.small,
            color: PDF_COLORS.textMuted,
          },
          {
            text: `Rédigé à IGNY, le ${formatDateFr()}`,
            fontSize: PDF_FONT_SIZES.body,
            margin: [0, 6, 0, 0] as [number, number, number, number],
          },
        ],
      },
      ...(signatureDataUrl
        ? [
            {
              width: 109 as const,
              image: signatureDataUrl,
              fit: [108, 108] as [number, number],
            },
          ]
        : []),
      { width: "*", text: "" },
    ],
    margin: [0, 45, 0, 0] as [number, number, number, number],
  } as Content);

  // Nota Bene (texte libre, affiché à gauche sous la signature)
  if (rapport.notaBene && rapport.notaBene.trim()) {
    out.push({
      stack: [
        {
          text: "Nota Bene :",
          fontSize: PDF_FONT_SIZES.small,
          bold: true,
          color: PDF_COLORS.text,
          margin: [0, 0, 0, 2] as [number, number, number, number],
        },
        {
          text: rapport.notaBene,
          fontSize: PDF_FONT_SIZES.small,
          color: PDF_COLORS.text,
          italics: true,
        },
      ],
      margin: [0, 24, 0, 0] as [number, number, number, number],
    } as Content);
  }

  return out;
}
